import { useCallback, useState } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { attachJob } from '@/features/ai/jobsSlice'
import { useStartBlockEditMutation } from '@/features/ai/studioApi'
import { parseStartError, type StartFailure } from '@/features/ai/lib/parseStartError'
import { BLOCK_EDIT_PROMPT_MAX } from '@/features/ai/lib/promptLimits'

export interface BlockEditInput {
  courseId: number
  /** Порядок = порядок правок и событий в ленте (§6.1) — не сортировать. */
  blockIds: number[]
  prompt: string
}

/**
 * Запуск правки блоков промптом. Отдельный хук, а не ветка
 * `useStartGeneration`: другое тело, другой ответ, другой исход.
 *
 * Дубли в `block_ids` схлопывает СЕРВЕР, и знаменатель прогресса берётся из
 * его `blocks_total`, а не из длины нашего списка — иначе «3 из 3» на плане
 * из одного блока.
 */
export const useStartBlockEdit = () => {
  const dispatch = useAppDispatch()
  const [startBlockEdit, { isLoading }] = useStartBlockEditMutation()
  const [failure, setFailure] = useState<StartFailure | null>(null)

  const start = useCallback(
    async ({ courseId, blockIds, prompt }: BlockEditInput): Promise<boolean> => {
      setFailure(null)
      const trimmed = prompt.trim()
      // Пустой ввод и переросший промпт — не отказ сервера, а «ничего не
      // произошло»: кнопка на них и так погашена, `failure` не выставляем.
      if (blockIds.length === 0 || !trimmed || trimmed.length > BLOCK_EDIT_PROMPT_MAX) return false
      try {
        const started = await startBlockEdit({
          course_id: courseId,
          block_ids: blockIds,
          prompt: trimmed,
        }).unwrap()

        // `attachJob` сам чистит историю прошлого прогона — инвариант живёт в
        // `chatSlice`, своей чистки под-проект E не заводит. Только после
        // успешного ответа: упавший старт не имеет права стереть итог,
        // который автор ещё не прочитал.
        dispatch(
          attachJob({
            courseId,
            entry: {
              jobId: started.job_id,
              type: 'course_block_edit',
              startedAt: Date.now(),
              // Джоба параметров запуска не возвращает (§1.2) — храним у себя;
              // по ним же панель узнаёт, какие блоки заняты прогоном.
              params: { kind: 'course_block_edit', block_ids: blockIds, prompt: trimmed },
              total: started.blocks_total ?? null,
            },
          }),
        )
        return true
      } catch (error) {
        setFailure(parseStartError(error))
        return false
      }
    },
    [startBlockEdit, dispatch],
  )

  return { start, isStarting: isLoading, failure, reset: () => setFailure(null) }
}
